import { z } from 'zod';

/**
 * Parses JSON-string tool arguments into plain objects when LLMs stringify nested inputs.
 * @param value Raw tool argument value.
 * @param role Field name used in error messages (`select`, `where`, etc.).
 * @returns Parsed value, or the original value when it is not a string.
 * @throws Error when the string is not valid JSON.
 */
export function coerceAiToolJsonObjectInput(value: unknown, role: string): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value) as unknown;
  } catch {
    throw new Error(
      `${role} must be a JSON object, not a string. Pass a nested object in tool arguments.`,
    );
  }
}

/**
 * Zod preprocess that parses JSON-string tool arguments for nested objects or arrays.
 * @param role Field name used in error messages (`prompt`, `childPrompts`, etc.).
 */
export function preprocessAiToolNestedParam(role: string): (value: unknown) => unknown {
  return (value: unknown) => {
    if (value === undefined) {
      return undefined;
    }
    return coerceAiToolJsonObjectInput(value, role);
  };
}

/**
 * Wraps a Zod schema so stringified nested tool parameters are parsed before validation.
 * @param schema Inner schema for the nested object or array.
 * @param role Field name used in error messages.
 */
export function withAiToolNestedParamSchema<T extends z.ZodType>(
  schema: T,
  role: string,
): z.ZodType<z.output<T>, unknown> {
  return z.preprocess(preprocessAiToolNestedParam(role), schema);
}

/** Filter comparison operators exposed to AI tools (not raw WhereInput). */
export const AI_TOOL_FILTER_OPS = [
  'eq',
  'ne',
  'contains',
  'in',
  'gt',
  'gte',
  'lt',
  'lte',
  'isNull',
  'isNotNull',
] as const;

export type AiToolFilterOp = (typeof AI_TOOL_FILTER_OPS)[number];

const aiToolFilterOpSchema = z.enum(AI_TOOL_FILTER_OPS);

/** Primitive value categories used to constrain agent-facing filter operators. */
export type AiToolFilterValueType = 'string' | 'number' | 'boolean' | 'date' | 'enum';

/** Leaf filter clause in the AI tool filter DSL. */
export type AiToolFilterClause = {
  field: string;
  op: AiToolFilterOp;
  value?: unknown;
};

/** Compound filter node (`and` / `or` / `not`) or a leaf clause. */
export type AiToolFilterInput = {
  and?: AiToolFilterInput[];
  or?: AiToolFilterInput[];
  not?: AiToolFilterInput;
} & Partial<AiToolFilterClause>;

/** Flat field mask: only `{ fieldName: true }` at one level (no nested objects). */
export type AiToolFieldMask = Readonly<Record<string, true>>;

/** Typed orderBy input for AI tools. */
export type AiToolOrderByInput = {
  field: string;
  direction: 'asc' | 'desc';
};

/** Maps an agent-facing filter field to a where path (nested keys). */
export type AiToolFilterFieldMapping = {
  readonly path: readonly string[];
  readonly ops?: readonly AiToolFilterOp[];
  readonly valueType?: AiToolFilterValueType;
};

/** Config for expanding a flat field mask into a `select`. */
export type AiToolFieldMaskExpansionConfig = {
  readonly defaultSelect: Readonly<Record<string, unknown>>;
  readonly scalarFields: readonly string[];
  readonly relationFields?: readonly string[];
  readonly relationExpansions?: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
};

/**
 * Guidance for AI tool descriptions: only pass `select` when deviating from the default field mask.
 */
export const AI_TOOL_SELECT_USAGE_NOTE =
  'Only pass `select` when you need fields outside the default result mask. Omit it otherwise; use read tools for full records. Pass a flat object of `{ fieldName: true }` flags only — no nested select or include.';

/**
 * Guidance for findMany AI tool descriptions: optional filter DSL with match-all defaults.
 */
export const AI_TOOL_WHERE_USAGE_NOTE =
  'Optional structured filter (not raw WhereInput). Omit `where` to match all rows. Leaf: `{ field, op, value? }`. Groups: `{ and: [...] }` or `{ or: [...] }`. Example: `{ field: "group", op: "eq", value: "system" }`. Empty `{ and: [] }` also matches all.';

/** Shared row limit schema for find/search AI tools (coerces numeric strings). */
export const aiToolRowLimitSchema = z.coerce
  .number()
  .int()
  .min(1)
  .max(200)
  .default(50)
  .describe('Maximum rows to return (default 50, max 200).');

const aiToolOrderDirectionSchema = z.enum(['asc', 'desc']);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function assertAtLeastOneMaskField(mask: AiToolFieldMask, role: string): void {
  if (Object.keys(mask).length === 0) {
    throw new Error(`${role} must include at least one \`true\` field when provided.`);
  }
}

function formatUnknownFilterFieldMessage(field: string, allowedFields: readonly string[]): string {
  const allowed = [...allowedFields].join(', ');
  if (field === 'version') {
    return `Unknown filter field "version". Allowed: ${allowed}. Version numbers are on PromptVersion, not Prompt — use readPrompt with \`versions: true\` for version history.`;
  }
  return `Unknown filter field "${field}". Allowed: ${allowed}.`;
}

/**
 * Parses and normalizes agent `where` input: JSON strings, empty objects, and `{ and: [] }` mean match-all.
 * @param value Raw tool argument value.
 * @returns Parsed filter object, or `undefined` when the filter should match all rows.
 */
export function normalizeAiToolWhereInput(value: unknown): unknown {
  if (value === undefined) {
    return undefined;
  }

  const parsed = typeof value === 'string' ? coerceAiToolJsonObjectInput(value, 'where') : value;

  if (!isPlainObject(parsed)) {
    return parsed;
  }

  if (Object.keys(parsed).length === 0) {
    return undefined;
  }

  if (
    Array.isArray(parsed.and) &&
    parsed.and.length === 0 &&
    parsed.or === undefined &&
    parsed.not === undefined &&
    parsed.field === undefined
  ) {
    return undefined;
  }

  if (
    Array.isArray(parsed.or) &&
    parsed.or.length === 0 &&
    parsed.and === undefined &&
    parsed.not === undefined &&
    parsed.field === undefined
  ) {
    return undefined;
  }

  return parsed;
}

function preprocessAiToolSelectInput(value: unknown): unknown {
  if (value === undefined) {
    return undefined;
  }

  const parsed = coerceAiToolJsonObjectInput(value, 'select');

  if (isPlainObject(parsed) && Object.prototype.hasOwnProperty.call(parsed, 'version')) {
    throw new Error(
      'select.version is not a Prompt field. Use `versions: true` to load PromptVersion history.',
    );
  }

  return parsed;
}

function preprocessAiToolOrderByInput(value: unknown): unknown {
  if (value === undefined) {
    return undefined;
  }

  return coerceAiToolJsonObjectInput(value, 'orderBy');
}

/**
 * Creates a Zod schema for an optional flat boolean field mask.
 * @param scalarFields Scalar model fields agents may request.
 * @param relationFields Relation flags the server expands to canonical selects.
 */
export function createAiToolFieldMaskSchema(
  scalarFields: readonly string[],
  relationFields: readonly string[] = [],
): z.ZodType<AiToolFieldMask | undefined, unknown> {
  const allowed = new Set([...scalarFields, ...relationFields]);
  const shape: Record<string, z.ZodOptional<z.ZodLiteral<true>>> = {};
  for (const field of allowed) {
    shape[field] = z.literal(true).optional();
  }

  return z
    .preprocess(preprocessAiToolSelectInput, z.object(shape).strict().optional())
    .superRefine((mask, context) => {
      if (mask === undefined) {
        return;
      }
      if (!isPlainObject(mask)) {
        context.addIssue({
          code: 'custom',
          message: 'select must be a non-null plain object.',
        });
        return;
      }
      if (Object.keys(mask).length === 0) {
        context.addIssue({
          code: 'custom',
          message: 'select must include at least one `true` field when provided.',
        });
        return;
      }
      for (const [key, value] of Object.entries(mask)) {
        if (value !== true) {
          context.addIssue({
            code: 'custom',
            message: `select.${key} must be \`true\`. Nested objects and \`include\` are not supported.`,
          });
        }
      }
    })
    .transform((mask) => mask as AiToolFieldMask | undefined)
    .describe(
      `Optional flat field mask: \`{ fieldName: true }\` for allowed fields (${[...allowed].join(', ')}). ${AI_TOOL_SELECT_USAGE_NOTE}`,
    );
}

/**
 * Creates a Zod schema for typed orderBy input.
 * @param sortableFields Fields agents may sort by for this tool.
 */
export function createAiToolOrderBySchema(
  sortableFields: readonly string[],
): z.ZodOptional<z.ZodType<AiToolOrderByInput | undefined>> {
  if (sortableFields.length === 0) {
    return z.never().optional();
  }

  return z
    .preprocess(
      preprocessAiToolOrderByInput,
      z
        .object({
          field: z.enum(sortableFields as [string, ...string[]]),
          direction: aiToolOrderDirectionSchema,
        })
        .optional(),
    )
    .describe(
      `Optional sort: \`{ field, direction }\` where field is one of ${sortableFields.join(', ')}.`,
    ) as unknown as z.ZodOptional<z.ZodType<AiToolOrderByInput | undefined>>;
}

function createAiToolFilterNodeSchema(
  allowedFields: readonly string[],
  fieldMappings: Readonly<Record<string, AiToolFilterFieldMapping>>,
): z.ZodType<AiToolFilterInput> {
  const allowed = new Set(allowedFields);
  const filterOpsByValueType = {
    boolean: ['eq', 'ne', 'in', 'isNull', 'isNotNull'],
    date: ['eq', 'ne', 'in', 'gt', 'gte', 'lt', 'lte', 'isNull', 'isNotNull'],
    enum: ['eq', 'ne', 'in', 'isNull', 'isNotNull'],
    number: ['eq', 'ne', 'in', 'gt', 'gte', 'lt', 'lte', 'isNull', 'isNotNull'],
    string: ['eq', 'ne', 'contains', 'in', 'isNull', 'isNotNull'],
  } as const satisfies Record<AiToolFilterValueType, readonly AiToolFilterOp[]>;

  function valueMatchesType(value: unknown, valueType: AiToolFilterValueType): boolean {
    switch (valueType) {
      case 'boolean':
        return typeof value === 'boolean';
      case 'date':
        return typeof value === 'string' || value instanceof Date;
      case 'enum':
      case 'string':
        return typeof value === 'string';
      case 'number':
        return typeof value === 'number';
      default: {
        const _exhaustive: never = valueType;
        throw new Error(`Unsupported filter value type: ${String(_exhaustive)}`);
      }
    }
  }

  const leafSchema = z
    .object({
      field: z.string(),
      op: aiToolFilterOpSchema,
      value: z.unknown().optional(),
    })
    .strict()
    .superRefine((clause, context) => {
      if (!allowed.has(clause.field)) {
        context.addIssue({
          code: 'custom',
          message: formatUnknownFilterFieldMessage(clause.field, allowedFields),
        });
        return;
      }

      const mapping = fieldMappings[clause.field];
      const allowedOps =
        mapping?.ops ?? (mapping?.valueType ? filterOpsByValueType[mapping.valueType] : undefined);
      if (allowedOps !== undefined && !allowedOps.includes(clause.op)) {
        context.addIssue({
          code: 'custom',
          message: `Operator "${clause.op}" is not allowed on field "${clause.field}".`,
        });
      }

      if ((clause.op === 'isNull' || clause.op === 'isNotNull') && clause.value !== undefined) {
        context.addIssue({
          code: 'custom',
          message: `"${clause.op}" must not include a value.`,
        });
      }

      if (clause.op !== 'isNull' && clause.op !== 'isNotNull' && clause.value === undefined) {
        context.addIssue({
          code: 'custom',
          message: `Operator "${clause.op}" requires a value.`,
        });
      }

      if (clause.op === 'in' && !Array.isArray(clause.value)) {
        context.addIssue({
          code: 'custom',
          message: 'Operator "in" requires an array value.',
        });
      }

      const valueType = mapping?.valueType;
      if (valueType === undefined || clause.value === undefined) {
        return;
      }

      if (clause.op === 'in') {
        if (
          Array.isArray(clause.value) &&
          clause.value.some((item) => !valueMatchesType(item, valueType))
        ) {
          context.addIssue({
            code: 'custom',
            message: `Operator "in" values for "${clause.field}" must be ${valueType} values.`,
          });
        }
        return;
      }

      if (!valueMatchesType(clause.value, valueType)) {
        context.addIssue({
          code: 'custom',
          message: `Filter value for "${clause.field}" must be a ${valueType}.`,
        });
      }
    });

  const compoundSchema: z.ZodType<AiToolFilterInput> = z.lazy(() =>
    z
      .object({
        and: z.array(compoundSchema).min(1).optional(),
        or: z.array(compoundSchema).min(1).optional(),
        not: compoundSchema.optional(),
        field: z.string().optional(),
        op: aiToolFilterOpSchema.optional(),
        value: z.unknown().optional(),
      })
      .strict()
      .superRefine((node, context) => {
        const isLeaf = node.field !== undefined;
        const compoundKeys = [node.and, node.or, node.not].filter(
          (compoundValue) => compoundValue !== undefined,
        );
        const hasCompound = compoundKeys.length > 0;

        if (isLeaf && hasCompound) {
          context.addIssue({
            code: 'custom',
            message: 'Filter nodes cannot combine leaf fields (field/op) with and/or/not.',
          });
          return;
        }

        if (compoundKeys.length > 1) {
          context.addIssue({
            code: 'custom',
            message: 'Filter nodes must use only one of and, or, or not.',
          });
          return;
        }

        if (isLeaf) {
          const leafResult = leafSchema.safeParse(node);
          if (!leafResult.success) {
            for (const issue of leafResult.error.issues) {
              context.addIssue({ code: 'custom', message: issue.message, path: issue.path });
            }
          }
          return;
        }

        if (!hasCompound) {
          context.addIssue({
            code: 'custom',
            message: 'Filter must be a leaf { field, op, value? } or use and/or/not.',
          });
        }
      }),
  );

  return compoundSchema;
}

/**
 * Creates a Zod schema for the AI tool filter DSL `where` clause.
 * @param allowedFields Agent-facing filter field names.
 * @param fieldMappings Optional per-field where path and allowed operators.
 */
export function createAiToolFilterSchema(
  allowedFields: readonly string[],
  fieldMappings: Readonly<Record<string, AiToolFilterFieldMapping>> = {},
): z.ZodType<AiToolFilterInput> {
  return z
    .preprocess(
      normalizeAiToolWhereInput,
      createAiToolFilterNodeSchema(allowedFields, fieldMappings),
    )
    .describe(AI_TOOL_WHERE_USAGE_NOTE) as unknown as z.ZodType<AiToolFilterInput>;
}

/**
 * Shared pagination fields for findMany AI tools.
 */
export const findManyPaginationSchema = {
  take: aiToolRowLimitSchema,
  skip: z.coerce
    .number()
    .int()
    .min(0)
    .optional()
    .describe('Rows to skip before returning results.'),
} as const;

/** Indicates whether a find tool used typo-tolerant search after no precise matches. */
export const searchFallbackUsedSchema = z
  .boolean()
  .optional()
  .describe('True when typo-tolerant search ran after precise search returned zero rows.');

/**
 * Builds a findMany AI tool input schema with typed filter, select mask, and orderBy.
 */
export function createFindManyAiToolInputSchema(options: {
  filterableFields: readonly string[];
  filterFieldMappings?: Readonly<Record<string, AiToolFilterFieldMapping>>;
  sortableFields: readonly string[];
  selectableScalars: readonly string[];
  selectableRelations?: readonly string[];
}): z.ZodObject<{
  where: z.ZodOptional<z.ZodType<AiToolFilterInput | undefined>>;
  select: ReturnType<typeof createAiToolFieldMaskSchema>;
  take: typeof findManyPaginationSchema.take;
  skip: typeof findManyPaginationSchema.skip;
  orderBy: ReturnType<typeof createAiToolOrderBySchema>;
  searchText: z.ZodOptional<z.ZodString>;
}> {
  const filterSchema = createAiToolFilterNodeSchema(
    options.filterableFields,
    options.filterFieldMappings ?? {},
  );

  return z.object({
    where: z
      .preprocess(normalizeAiToolWhereInput, filterSchema.optional())
      .describe(AI_TOOL_WHERE_USAGE_NOTE),
    select: createAiToolFieldMaskSchema(
      options.selectableScalars,
      options.selectableRelations ?? [],
    ),
    ...findManyPaginationSchema,
    orderBy: createAiToolOrderBySchema(options.sortableFields),
    searchText: z
      .string()
      .min(1)
      .max(500)
      .optional()
      .describe(
        'Optional free-text search over the model searchFields (precise indexed first; typo-tolerant fuzzy only when precise returns zero rows).',
      ),
  }) as unknown as z.ZodObject<{
    where: z.ZodOptional<z.ZodType<AiToolFilterInput | undefined>>;
    select: ReturnType<typeof createAiToolFieldMaskSchema>;
    take: typeof findManyPaginationSchema.take;
    skip: typeof findManyPaginationSchema.skip;
    orderBy: ReturnType<typeof createAiToolOrderBySchema>;
    searchText: z.ZodOptional<z.ZodString>;
  }>;
}

function nestConditionAtPath(
  path: readonly string[],
  condition: Record<string, unknown>,
): Record<string, unknown> {
  if (path.length === 0) {
    return condition;
  }

  const [head, ...rest] = path;
  if (head === undefined) {
    return condition;
  }

  return { [head]: nestConditionAtPath(rest, condition) };
}

function buildLeafFilterCondition(op: AiToolFilterOp, value: unknown): Record<string, unknown> {
  switch (op) {
    case 'eq':
      return { equals: value };
    case 'ne':
      return { not: value };
    case 'contains':
      return { contains: value, mode: 'insensitive' };
    case 'in':
      return { in: value };
    case 'gt':
      return { gt: value };
    case 'gte':
      return { gte: value };
    case 'lt':
      return { lt: value };
    case 'lte':
      return { lte: value };
    case 'isNull':
      return { equals: null };
    case 'isNotNull':
      return { not: null };
    default: {
      const _exhaustive: never = op;
      throw new Error(`Unsupported filter operator: ${String(_exhaustive)}`);
    }
  }
}

/**
 * Translates one leaf filter clause to a where fragment.
 */
export function translateAiToolFilterClause(
  clause: AiToolFilterClause,
  fieldMappings: Readonly<Record<string, AiToolFilterFieldMapping>>,
): Record<string, unknown> {
  const mapping = fieldMappings[clause.field];
  const path = mapping?.path ?? [clause.field];
  const leafCondition = buildLeafFilterCondition(clause.op, clause.value);

  if (path.length === 1 && path[0] === clause.field) {
    return { [clause.field]: leafCondition };
  }

  const lastKey = path[path.length - 1];
  if (lastKey === undefined) {
    throw new Error(`Invalid filter mapping for field "${clause.field}".`);
  }

  return nestConditionAtPath(path.slice(0, -1), { [lastKey]: leafCondition });
}

/**
 * Translates the AI tool filter DSL into an ORM where object.
 */
export function translateAiToolFilterToWhere(
  filter: AiToolFilterInput | undefined,
  fieldMappings: Readonly<Record<string, AiToolFilterFieldMapping>> = {},
): Record<string, unknown> {
  if (filter === undefined) {
    return {};
  }

  if (filter.field !== undefined) {
    if (filter.op === undefined) {
      throw new Error('Filter leaf requires both field and op.');
    }
    return translateAiToolFilterClause(
      { field: filter.field, op: filter.op, value: filter.value },
      fieldMappings,
    );
  }

  if (filter.and !== undefined) {
    return {
      AND: filter.and.map((child) => translateAiToolFilterToWhere(child, fieldMappings)),
    };
  }

  if (filter.or !== undefined) {
    return {
      OR: filter.or.map((child) => translateAiToolFilterToWhere(child, fieldMappings)),
    };
  }

  if (filter.not !== undefined) {
    return {
      NOT: translateAiToolFilterToWhere(filter.not, fieldMappings),
    };
  }

  throw new Error('Filter must be a leaf clause or use and/or/not.');
}

/**
 * Expands a flat field mask into a `select`, or returns the default when omitted.
 */
export function expandAiToolFieldMask(
  mask: AiToolFieldMask | undefined,
  config: AiToolFieldMaskExpansionConfig,
): Record<string, unknown> {
  if (mask === undefined) {
    return { ...config.defaultSelect };
  }

  assertAtLeastOneMaskField(mask, 'select');

  const allowedScalars = new Set(config.scalarFields);
  const allowedRelations = new Set(config.relationFields ?? []);
  const select: Record<string, unknown> = {};

  for (const field of Object.keys(mask)) {
    if (!allowedScalars.has(field) && !allowedRelations.has(field)) {
      throw new Error(
        `Unknown select field "${field}". Allowed: ${[...allowedScalars, ...allowedRelations].join(', ')}.`,
      );
    }

    if (allowedRelations.has(field)) {
      const expansion = config.relationExpansions?.[field];
      if (expansion === undefined) {
        throw new Error(`Relation field "${field}" is not expandable for this tool.`);
      }
      select[field] = expansion;
      continue;
    }

    select[field] = true;
  }

  return select;
}

/**
 * Translates typed orderBy input to an orderBy object.
 *
 * Every orderBy path is a single segment: ZenStack 3 resolves delegate inheritance natively, so no
 * mapping table needs the v2 `delegate_aux_*` hop that multi-segment paths existed to express. A
 * longer path is therefore a malformed mapping and throws rather than being silently nested.
 * @param orderBy Optional typed orderBy from tool input.
 * @param defaultOrderBy orderBy used when `orderBy` is omitted.
 * @param fieldPaths Optional map from agent field names to single-segment orderBy paths.
 */
export function translateAiToolOrderBy(
  orderBy: AiToolOrderByInput | undefined,
  defaultOrderBy: Record<string, 'asc' | 'desc'> | ReadonlyArray<Record<string, 'asc' | 'desc'>>,
  fieldPaths: Readonly<Record<string, readonly string[]>> = {},
): Record<string, unknown> | ReadonlyArray<Record<string, unknown>> {
  if (orderBy === undefined) {
    return Array.isArray(defaultOrderBy) ? [...defaultOrderBy] : { ...defaultOrderBy };
  }

  const path = fieldPaths[orderBy.field] ?? [orderBy.field];
  const field = path[0];
  if (path.length !== 1 || field === undefined) {
    throw new Error(`Invalid orderBy field "${orderBy.field}".`);
  }

  return { [field]: orderBy.direction };
}

/**
 * Maps common NamedItem fields inherited by ZenStack polymorphic models.
 *
 * ZenStack 3 resolves delegate inheritance natively: a child model's own `where` accepts the base
 * model's fields directly. The v2 `delegate_aux_namedItem` hop these paths used to carry no longer
 * exists in the generated types, so every path here is a single segment.
 */
export const namedItemFilterFieldMappings = {
  name: { path: ['name'], valueType: 'string' },
  slug: { path: ['slug'], valueType: 'string' },
  publishedAt: { path: ['publishedAt'], valueType: 'date' },
  private: { path: ['private'], valueType: 'boolean' },
  ownerId: { path: ['ownerId'], valueType: 'string' },
  spaceId: { path: ['spaceId'], valueType: 'string' },
} as const satisfies Record<string, AiToolFilterFieldMapping>;

/** Maps common NamedItem fields for orderBy translation (see above: v3 needs no delegate hop). */
export const namedItemOrderByFieldPaths = {
  name: ['name'],
  slug: ['slug'],
  publishedAt: ['publishedAt'],
  createdAt: ['createdAt'],
  updatedAt: ['updatedAt'],
} as const satisfies Record<string, readonly string[]>;
